/**
 * A player's card (desktop sidebar) or chip (tablet/mobile top bar): name,
 * presence, score, rack count, series wins, the turn glow, the timer on the
 * active player and a "thinking…" indicator for the opponent.
 */
import clsx from 'clsx';
import type { GameView, PublicPlayer, Seat, SeatView } from '@wordloom/engine';
import { AnimatedNumber } from './AnimatedNumber';
import { Timer } from './Timer';
import './PlayerCard.css';

interface PlayerCardProps {
  seat: Seat;
  seatView: SeatView | null;
  player: PublicPlayer | null;
  isYou: boolean;
  game: GameView | null;
  variant?: 'card' | 'chip';
}

function initial(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

export function PlayerCard({ seat, seatView, player, isYou, game, variant = 'card' }: PlayerCardProps) {
  const name = seatView?.name ?? player?.name ?? 'Waiting…';
  const playing = game?.status === 'playing';
  const active = playing && game?.currentSeat === seat;
  const connected = seatView?.connected ?? false;
  const score = player?.score ?? 0;
  const wins = seatView?.wins ?? 0;
  const thinking = active && !isYou && connected;
  const arranging = seatView?.activity === 'arranging';
  const rackCount = player?.rackCount ?? 0;

  const status = active ? (isYou ? 'your turn' : 'their turn') : '';
  const label = [
    `${name}${isYou ? ' (you)' : ''}`,
    `${score} ${score === 1 ? 'point' : 'points'}`,
    connected ? null : 'offline',
    status || null,
  ]
    .filter(Boolean)
    .join(', ');

  const timer =
    active && game ? (
      <Timer
        deadline={game.turnDeadline}
        turnStartedAt={game.turnStartedAt}
        limitSec={game.settings.turnTimeLimitSec}
        mine={isYou}
        size={variant === 'chip' ? 'sm' : 'md'}
      />
    ) : null;

  const thinkingDots = thinking ? (
    <span className="player__thinking" aria-hidden="true">
      <span className="dots">
        <i />
        <i />
        <i />
      </span>
      {variant === 'card' && <span>{arranging ? 'arranging tiles…' : 'thinking…'}</span>}
    </span>
  ) : null;

  return (
    <section
      className={clsx(
        'player',
        `player--${variant}`,
        `player--seat${seat}`,
        isYou && 'is-you',
        active && 'is-active',
        !active && playing && 'is-idle',
      )}
      data-player-card={seat}
      aria-label={label}
    >
      <span className="player__avatar" aria-hidden="true">
        {initial(name)}
        <span className={clsx('player__presence', connected ? 'is-online' : 'is-away')} />
      </span>
      <span className="player__who">
        <span className="player__name">
          {/* Long names are clamped (card) or ellipsized (chip): the title shows them in full. */}
          <span className="player__name-text" title={name}>
            {name}
          </span>
          {isYou && <span className="player__you">You</span>}
        </span>
        {variant === 'card' ? (
          <span className="player__meta">
            {connected ? (
              <>
                {rackCount} {rackCount === 1 ? 'tile' : 'tiles'}
                {wins > 0 && (
                  <>
                    {' · '}
                    {wins} {wins === 1 ? 'win' : 'wins'}
                  </>
                )}
              </>
            ) : (
              <span className="player__away">Disconnected</span>
            )}
          </span>
        ) : (
          <span className="player__chip-meta">
            {connected ? `${rackCount} ${rackCount === 1 ? 'tile' : 'tiles'}` : 'Away'}
          </span>
        )}
      </span>
      <span className="player__score" aria-hidden="true">
        <AnimatedNumber value={score} />
      </span>
      {variant === 'card' && (timer || thinkingDots) && (
        <span className="player__turn">
          {thinkingDots}
          {timer}
        </span>
      )}
      {variant === 'chip' && (timer || thinkingDots) && (
        <span className="player__chip-timer">
          {thinkingDots}
          {timer}
        </span>
      )}
    </section>
  );
}
